import React, { useState } from "react";
import { Plus, Trash2, MapPin, Save } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { toast } from "sonner";

const EMPTY_ADDR = {
  label: "Home",
  recipient: "",
  phone: "",
  country: "United States",
  region: "",
  city: "",
  zip: "",
  street: "",
  notes: "",
};

export default function AccountAddresses() {
  const { customer, updateProfile } = useCustomerAuth();
  const [list, setList] = useState(customer?.addresses || []);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_ADDR);
  const [saving, setSaving] = useState(false);

  const startNew = () => { setDraft(EMPTY_ADDR); setEditing("new"); };
  const startEdit = (i) => { setDraft({ ...EMPTY_ADDR, ...list[i] }); setEditing(i); };

  const persist = async (newList) => {
    setSaving(true);
    const res = await updateProfile({ addresses: newList });
    setSaving(false);
    if (res.ok) {
      setList(newList);
      toast.success("Addresses saved");
      setEditing(null);
    } else {
      toast.error(res.error || "Couldn't save");
    }
  };

  const save = () => {
    if (!draft.street || !draft.city) {
      toast.error("Add at least street and city");
      return;
    }
    const next = editing === "new" ? [...list, draft] : list.map((a, i) => (i === editing ? draft : a));
    persist(next);
  };

  const remove = (i) => {
    if (!window.confirm("Delete this address?")) return;
    persist(list.filter((_, idx) => idx !== i));
  };

  return (
    <div data-testid="account-addresses-page">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B5158]">Saved</div>
          <h2 className="font-display text-3xl">Addresses</h2>
        </div>
        {editing === null && (
          <button onClick={startNew} className="inline-flex items-center gap-1.5 rounded-full border border-[#2B2127] bg-white px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#2B2127] transition-colors hover:bg-[#2B2127] hover:text-white" data-testid="addr-new-btn">
            <Plus size={12} /> New address
          </button>
        )}
      </div>

      {editing !== null && (
        <div className="mb-6 rounded-md border border-[#E6D4CC] bg-white p-5" data-testid="addr-form">
          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Label" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} testId="addr-label" />
            <Input label="Recipient" value={draft.recipient} onChange={(v) => setDraft({ ...draft, recipient: v })} testId="addr-recipient" />
            <Input label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} testId="addr-phone" />
            <Input label="Country" value={draft.country} onChange={(v) => setDraft({ ...draft, country: v })} testId="addr-country" />
            <Input label="Region / State" value={draft.region} onChange={(v) => setDraft({ ...draft, region: v })} testId="addr-region" />
            <Input label="City *" value={draft.city} onChange={(v) => setDraft({ ...draft, city: v })} testId="addr-city" />
            <Input label="Postal code" value={draft.zip} onChange={(v) => setDraft({ ...draft, zip: v })} testId="addr-zip" />
            <Input label="Street *" value={draft.street} onChange={(v) => setDraft({ ...draft, street: v })} testId="addr-street" className="md:col-span-2" />
            <Input label="Notes" value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} testId="addr-notes" className="md:col-span-2" />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#2B2127] px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#9D5C6C] disabled:opacity-60"
              data-testid="addr-save"
            >
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(null)} className="rounded-full border border-[#E6D4CC] bg-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#2B2127] transition-colors hover:bg-[#F1E3DC]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <div className="rounded-md border border-[#E6D4CC] bg-white p-10 text-center" data-testid="addr-empty">
          <MapPin size={40} strokeWidth={1.2} className="mx-auto mb-4 text-[#6B5158]/30" />
          <h3 className="font-display text-2xl">No addresses saved</h3>
          <p className="mt-1 text-sm text-[#6B5158]">Add a delivery address to speed up future checkouts.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((a, i) => (
            <div key={i} className="rounded-md border border-[#E6D4CC] bg-white p-5" data-testid={`addr-card-${i}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">{a.label || "Address"}</span>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(i)} className="text-xs text-[#9D5C6C] hover:underline" data-testid={`addr-edit-${i}`}>Edit</button>
                  <button onClick={() => remove(i)} aria-label="Delete" className="ml-2 text-[#6B5158] hover:text-rose-600" data-testid={`addr-delete-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="text-sm text-[#2B2127]">
                {a.recipient && <>{a.recipient}<br /></>}
                {a.street}<br />
                {a.city}{a.region ? `, ${a.region}` : ""} {a.zip}<br />
                {a.country}
              </div>
              {a.phone && <div className="mt-2 text-xs text-[#6B5158]">Phone {a.phone}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[#E6D4CC] bg-white px-3 py-2 text-sm outline-none focus:border-[#2B2127]"
        data-testid={testId}
      />
    </label>
  );
}
